import { create } from 'zustand';
import type { WindowState, AppType, DriveFile, Project, AppSettings, Notification, FileTreeNode } from '../types';
import { api } from '../services/api';
import { MOCK_ENABLED } from '../services/mock';

let idCounter = 0;
const genId = () => `win-${++idCounter}-${Date.now()}`;

const SETTINGS_KEY = 'cloud-desktop-settings';

function loadStoredSettings(): AppSettings {
  const defaults: AppSettings = {
    theme: 'dark',
    fontSize: 14,
    terminalFont: 'Menlo, Monaco, monospace',
    terminalFontSize: 14,
    autoSave: true,
    autoSaveDelay: 1000,
    wallpaper: 'default',
  };
  try {
    const raw = localStorage.getItem(SETTINGS_KEY);
    if (raw) return { ...defaults, ...JSON.parse(raw) };
  } catch {}
  return defaults;
}

interface DesktopStore {
  authenticated: boolean;
  authChecking: boolean;
  login: (password: string) => Promise<void>;
  logout: () => Promise<void>;
  checkAuth: () => Promise<void>;
  hasGoogleAccess: boolean;
  googleCheckDone: boolean;
  driveReauthNeeded: boolean;
  userEmail: string | null;

  windows: WindowState[];
  activeWindowId: string | null;
  nextZIndex: number;
  openApp: (appType: AppType, props?: Record<string, any>) => void;
  closeWindow: (id: string) => void;
  minimizeWindow: (id: string) => void;
  maximizeWindow: (id: string) => void;
  focusWindow: (id: string) => void;
  updateWindow: (id: string, updates: Partial<WindowState>) => void;
  closeAllWindows: () => void;

  currentFolderId: string | null;
  files: DriveFile[];
  fileTree: FileTreeNode;
  loadingFiles: boolean;
  loadFiles: (folderId?: string) => Promise<void>;
  loadFileTree: (folderId: string) => Promise<FileTreeNode>;
  setCurrentFolder: (folderId: string | null) => void;

  projects: Project[];
  currentProject: Project | null;
  loadingProjects: boolean;
  loadProjects: () => Promise<void>;
  createProject: (name: string) => Promise<Project>;
  openProject: (project: Project) => Promise<void>;
  deleteProject: (id: string) => Promise<void>;
  syncProject: (id: string, direction: 'up' | 'down') => Promise<void>;
  refreshCurrentProject: () => Promise<void>;
  checkGoogleAccess: () => Promise<void>;

  settings: AppSettings;
  updateSettings: (settings: Partial<AppSettings>) => void;

  notifications: Notification[];
  addNotification: (title: string, message: string, type?: Notification['type']) => void;
  removeNotification: (id: string) => void;

  theme: 'dark' | 'light';
  toggleTheme: () => void;

  commandPaletteOpen: boolean;
  toggleCommandPalette: () => void;

  appLauncherOpen: boolean;
  toggleAppLauncher: () => void;
}

export const useStore = create<DesktopStore>((set, get) => ({
  authenticated: false,
  authChecking: true,
  hasGoogleAccess: false,
  googleCheckDone: false,
  driveReauthNeeded: false,
  userEmail: null,

  login: async (password) => {
    await api.login(password);
    set({ authenticated: true });
    get().checkGoogleAccess();
  },
  logout: async () => {
    try { await api.logout(); } catch {}
    set({ authenticated: false, windows: [], activeWindowId: null, currentProject: null });
  },
  checkAuth: async () => {
    if (MOCK_ENABLED) {
      set({ authenticated: true, authChecking: false, hasGoogleAccess: true, googleCheckDone: true });
      return;
    }
    try {
      const res = await api.checkAuth();
      set({ authenticated: res.authenticated, authChecking: false, userEmail: res.email || null });
      if (res.authenticated) get().checkGoogleAccess();
    } catch {
      set({ authenticated: false, authChecking: false });
    }
  },
  checkGoogleAccess: async () => {
    try {
      const res = await api.checkAuth();
      const had = get().hasGoogleAccess;
      set({ hasGoogleAccess: !!res.hasGoogleAccess, googleCheckDone: true, userEmail: res.email || get().userEmail });
      if (had && !res.hasGoogleAccess) set({ driveReauthNeeded: true });
      if (res.hasGoogleAccess) set({ driveReauthNeeded: false });
    } catch {
      set({ hasGoogleAccess: false, googleCheckDone: true });
    }
  },

  windows: [],
  activeWindowId: null,
  nextZIndex: 100,
  openApp: (appType, props) => {
    const { windows, nextZIndex } = get();

    const key = appType + (props ? JSON.stringify(props) : '');
    const existing = windows.find(w => w.appType === appType && JSON.stringify(w.props || {}) === JSON.stringify(props || {}));
    if (existing) {
      get().focusWindow(existing.id);
      return;
    }

    const titles: Record<AppType, string> = {
      terminal: 'Terminal',
      editor: props?.title || 'Code Editor',
      'file-manager': 'Files',
      browser: 'Browser',
      chat: 'AI Assistant',
      settings: 'Settings',
      projects: 'Projects',
      preview: props?.title || 'Preview',
      calculator: 'Calculator',
      minesweeper: 'Minesweeper',
    };

    const defaultSizes: Record<AppType, { width: number; height: number }> = {
      terminal: { width: 780, height: 480 },
      editor: { width: 950, height: 620 },
      'file-manager': { width: 820, height: 520 },
      browser: { width: 900, height: 600 },
      chat: { width: 420, height: 620 },
      settings: { width: 520, height: 460 },
      projects: { width: 640, height: 520 },
      preview: { width: 900, height: 620 },
      calculator: { width: 320, height: 480 },
      minesweeper: { width: 340, height: 460 },
    };

    const size = defaultSizes[appType];
    const offset = (windows.length % 8) * 24;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const x = Math.min(Math.max(60 + offset, 20), vw - size.width - 20);
    const y = Math.min(Math.max(40 + offset, 20), vh - size.height - 60);

    const newWindow: WindowState = {
      id: genId(),
      appType,
      title: titles[appType],
      x,
      y,
      width: Math.min(size.width, vw - 40),
      height: Math.min(size.height, vh - 80),
      minWidth: 350,
      minHeight: 250,
      isMinimized: false,
      isMaximized: false,
      zIndex: nextZIndex,
      props,
    };

    set({
      windows: [...windows, newWindow],
      activeWindowId: newWindow.id,
      nextZIndex: nextZIndex + 1,
    });
  },
  closeWindow: (id) => {
    set((state) => {
      const remaining = state.windows.filter((w) => w.id !== id);
      return {
        windows: remaining,
        activeWindowId: state.activeWindowId === id ? remaining.at(-1)?.id || null : state.activeWindowId,
      };
    });
  },
  minimizeWindow: (id) => {
    set((state) => {
      const win = state.windows.find((w) => w.id === id);
      if (win?.isMinimized) {
        return {
          windows: state.windows.map((w) => w.id === id ? { ...w, isMinimized: false } : w),
          activeWindowId: id,
          nextZIndex: state.nextZIndex + 1,
        };
      }
      return {
        windows: state.windows.map((w) => w.id === id ? { ...w, isMinimized: true } : w),
        activeWindowId: state.activeWindowId === id ? null : state.activeWindowId,
      };
    });
  },
  maximizeWindow: (id) => {
    set((state) => ({
      windows: state.windows.map((w) =>
        w.id === id ? { ...w, isMaximized: !w.isMaximized } : w
      ),
    }));
  },
  focusWindow: (id) => {
    const { nextZIndex } = get();
    set((state) => ({
      windows: state.windows.map((w) =>
        w.id === id ? { ...w, zIndex: nextZIndex, isMinimized: false } : w
      ),
      activeWindowId: id,
      nextZIndex: nextZIndex + 1,
    }));
  },
  updateWindow: (id, updates) => {
    set((state) => ({
      windows: state.windows.map((w) => (w.id === id ? { ...w, ...updates } : w)),
    }));
  },
  closeAllWindows: () => {
    set({ windows: [], activeWindowId: null });
  },

  currentFolderId: null,
  files: [],
  loadingFiles: false,
  loadFiles: async (folderId) => {
    set({ loadingFiles: true });
    try {
      const res = await api.listFiles(folderId);
      set({ files: res.files || res, loadingFiles: false, driveReauthNeeded: false });
      if (folderId !== undefined) set({ currentFolderId: folderId || null });
    } catch (err: any) {
      set({ loadingFiles: false });
      if (err?.reauth) {
        set({ driveReauthNeeded: true, hasGoogleAccess: false });
        get().addNotification('Reconnect needed', 'Google authorization expired — reconnect your account.', 'error');
      } else {
        get().addNotification('Error', 'Failed to load files', 'error');
      }
    }
  },
  loadFileTree: async (folderId) => {
    const buildTree = async (id: string, name: string): Promise<FileTreeNode> => {
      try {
        const res = await api.listFiles(id);
        const children: FileTreeNode[] = [];
        for (const file of res.files || res) {
          if (file.mimeType === 'application/vnd.google-apps.folder') {
            children.push(await buildTree(file.id, file.name));
          } else {
            children.push({
              id: file.id,
              name: file.name,
              type: 'file',
              size: parseInt(file.size || '0'),
              modifiedTime: file.modifiedTime,
            });
          }
        }
        return { id, name, type: 'folder', children };
      } catch {
        return { id, name, type: 'folder', children: [] };
      }
    };
    const tree = await buildTree(folderId, 'root');
    set({ fileTree: tree });
    return tree;
  },
  fileTree: { id: 'root', name: 'MyCloudProjects', type: 'folder', children: [] },
  setCurrentFolder: (folderId) => set({ currentFolderId: folderId }),

  projects: [],
  currentProject: null,
  loadingProjects: false,
  loadProjects: async () => {
    set({ loadingProjects: true });
    try {
      const res = await api.listProjects();
      set({ projects: res.projects || res, loadingProjects: false });
    } catch (err: any) {
      set({ loadingProjects: false });
      if (err.message !== 'Unauthorized') {
        get().addNotification('Error', 'Failed to load projects', 'error');
      }
    }
  },
  createProject: async (name) => {
    const res = await api.createProject(name);
    const project: Project = res.project || res;
    set((state) => {
      const exists = state.projects.some((p) => p.id === project.id);
      return { projects: exists ? state.projects : [...state.projects, project] };
    });
    get().addNotification('Success', `Project "${name}" created`, 'success');
    return project;
  },
  openProject: async (project) => {
    set({ currentProject: project });
    get().addNotification('Info', `Opened project "${project.name}"`, 'info');
  },
  deleteProject: async (id) => {
    await api.deleteProject(id);
    set((state) => ({
      projects: state.projects.filter((p) => p.id !== id),
      currentProject: state.currentProject?.id === id ? null : state.currentProject,
    }));
    get().addNotification('Info', 'Project deleted', 'info');
  },
  syncProject: async (id, direction) => {
    get().addNotification('Info', `Syncing project ${direction === 'up' ? 'to' : 'from'} Google Drive...`, 'info');
    try {
      if (direction === 'up') {
        await api.syncProjectUp(id);
      } else {
        await api.syncProjectDown(id);
      }
      get().addNotification('Success', 'Project synced successfully', 'success');
    } catch {
      get().addNotification('Error', 'Sync failed', 'error');
    }
  },
  refreshCurrentProject: async () => {
    const { currentProject, loadProjects } = get();
    if (currentProject) {
      await loadProjects();
      const fresh = get().projects.find((p) => p.id === currentProject.id);
      if (fresh) set({ currentProject: fresh });
    }
  },

  settings: loadStoredSettings(),
  updateSettings: (newSettings) => {
    set((state) => {
      const settings = { ...state.settings, ...newSettings };
      try {
        localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
      } catch {}
      return { settings };
    });
  },

  notifications: [],
  addNotification: (title, message, type = 'info') => {
    const id = `notif-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const notification: Notification = { id, title, message, type, timestamp: Date.now() };
    set((state) => ({ notifications: [...state.notifications, notification] }));
    setTimeout(() => get().removeNotification(id), 5000);
  },
  removeNotification: (id) => {
    set((state) => ({ notifications: state.notifications.filter((n) => n.id !== id) }));
  },

  theme: 'dark',
  toggleTheme: () => {
    set((state) => {
      const newTheme = state.theme === 'dark' ? 'light' : 'dark';
      document.documentElement.classList.toggle('dark', newTheme === 'dark');
      return { theme: newTheme };
    });
  },

  commandPaletteOpen: false,
  toggleCommandPalette: () => set((state) => ({ commandPaletteOpen: !state.commandPaletteOpen })),

  appLauncherOpen: false,
  toggleAppLauncher: () => set((state) => ({ appLauncherOpen: !state.appLauncherOpen })),
}));

// Handle auth reset
window.addEventListener('auth:unauthorized', () => {
  useStore.setState({ authenticated: false, windows: [], activeWindowId: null });
});